import React, { memo, useState } from 'react'
import { useEffectOnce } from 'react-use'
import { Box, Typography } from 'transfix-ui/core'
import getTenderTrackingInfo from 'apps/booking/utils/api/getTenderTrackingInfo'
import { useCallApi } from 'apps/marketplace/utils/hooks/useCallApi'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui'

interface ITenderTrackingInfo {
  shipperStatus: {
    acceptedToday?: string
    acceptedPerCadence?: string
  }
  acceptanceCriteria?: string
  laneId?: string
  shipperRateId?: string
}

interface ITenderTrackingInfoProps {
  shipmentId: number
}

export const TenderTrackingInfo = memo<ITenderTrackingInfoProps>(({ shipmentId }) => {
  const [tenderTrackingInfo, setTenderTrackingInfo] = useState<ITenderTrackingInfo>()
  const [showTenderTrackingInfo, setShowTenderTrackingInfo] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<String>()
  const { load: fetchInfo } = useCallApi({ apiCall: getTenderTrackingInfo })

  useEffectOnce(() => {
    fetchInfo({ shipmentId })
      .then(response => {
        const mappedTenderInfo = {
          shipperStatus: response.shipperStatus,
          acceptanceCriteria: response.acceptanceCriteria,
          laneId: response.laneId,
          shipperRateId: response.shipperRateId,
        }
        const { shipperStatus } = mappedTenderInfo
        let shouldShowInfo = !!shipperStatus.acceptedPerCadence || !!shipperStatus.acceptedToday
        shouldShowInfo =
          shouldShowInfo ||
          !!mappedTenderInfo.acceptanceCriteria ||
          !!mappedTenderInfo.laneId ||
          !!mappedTenderInfo.shipperRateId
        setShowTenderTrackingInfo(shouldShowInfo)
        setTenderTrackingInfo(mappedTenderInfo)
      })
      .catch(err => {
        setErrorMessage(err.message)
      })
  })

  return showTenderTrackingInfo ? (
    <Box mb={2}>
      <Box>
        <Typography variant='subtitle1' color='textPrimary'>
          Contract Acceptance Information
        </Typography>
      </Box>
      {errorMessage ? (
        <Box mb={3}>
          <InlineMessage message={errorMessage} type={INLINE_MESSAGE_VARIANTS.ALERT} />
        </Box>
      ) : (
        <>
          {(!!tenderTrackingInfo?.shipperStatus?.acceptedToday ||
            tenderTrackingInfo?.shipperStatus?.acceptedPerCadence) && (
            <Box mt={2}>
              <Typography variant='subtitle2' color='textSecondary'>
                Shipper Status
              </Typography>
              <Box mt={1}>{tenderTrackingInfo?.shipperStatus?.acceptedToday}</Box>
              <Box mt={1}>{tenderTrackingInfo?.shipperStatus?.acceptedPerCadence}</Box>
            </Box>
          )}
          {!!tenderTrackingInfo?.acceptanceCriteria && (
            <Box mt={2}>
              <Typography variant='subtitle2' color='textSecondary'>
                Acceptance Criteria
              </Typography>
              <Box mt={1}>{tenderTrackingInfo?.acceptanceCriteria}</Box>
            </Box>
          )}
          {!!tenderTrackingInfo?.laneId && (
            <Box mt={2}>
              <Typography variant='subtitle2' color='textSecondary'>
                Lane ID
              </Typography>
              <Box mt={1}>{tenderTrackingInfo?.laneId}</Box>
            </Box>
          )}
          {!!tenderTrackingInfo?.shipperRateId && (
            <Box mt={2}>
              <Typography variant='subtitle2' color='textSecondary'>
                Shipper Rate ID
              </Typography>
              <Box mt={1}>{tenderTrackingInfo?.shipperRateId}</Box>
            </Box>
          )}
        </>
      )}
    </Box>
  ) : (
    <></>
  )
})
